import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
// Importar critical.css primero para establecer reglas fundamentales
import './assets/critical.css' // Estilos críticos para evitar scroll horizontal
import './assets/global.css'  // Tailwind + estilos globales
// Importamos las anulaciones responsivas después de otros estilos para que tengan mayor prioridad
import './assets/responsive-overrides.css' // Importante: orden de importación afecta prioridad CSS
import { createHead } from '@vueuse/head'  // Import createHead function
import { checkAndApplyRedirects, handleExternalRedirects, setupRedirectTracking } from './utils/redirectUtils'
import { vClickOutside } from './directives/vClickOutside' // <-- Importar la directiva
// Importar i18n
import i18n from './i18n'
import Trans from './i18n/translation'
import { hasAnalyticsConsent, hasAdvertisingConsent, loadGoogleAnalytics, loadGoogleAdSense, setDefaultGoogleConsentMode } from './utils/analyticsConsent'

// ─────────────────────────────────────────────────────────────────────────────
// CAPTURA DE UTMs (atribución de origen)
// Guarda los parámetros UTM en localStorage la primera vez que llega el usuario.
// Se preservan durante toda la sesión y se pueden leer desde cualquier componente.
// Parámetros capturados: utm_source, utm_medium, utm_campaign, utm_content, utm_term, fbclid
// ─────────────────────────────────────────────────────────────────────────────
;(function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
    const hasUtm = UTM_KEYS.some(k => params.has(k));
    if (hasUtm) {
      const utmData = {};
      UTM_KEYS.forEach(k => { if (params.has(k)) utmData[k] = params.get(k); });
      utmData._captured_at = new Date().toISOString();
      utmData._landing_page = window.location.pathname;
      localStorage.setItem('coolliving_utm', JSON.stringify(utmData));
    }
  } catch (_) { /* localStorage puede estar bloqueado en algunos navegadores */ }
})();

// Check for redirects before app initialization
checkAndApplyRedirects();

// Check for external referrer redirects
const referrer = document.referrer;
const externalRedirect = handleExternalRedirects(referrer);
if (externalRedirect) {
  router.push(externalRedirect);
}

// Web Vitals monitoring in production
if (process.env.NODE_ENV === 'production' || process.env.VUE_APP_ENV === 'production') {
  // Usar import dinámico sin depender de la disponibilidad inmediata del módulo
  import('web-vitals')
    .then(({ getCLS, getFID, getLCP, getFCP, getTTFB }) => {
      const reportWebVital = ({ name, delta, id }) => {
        // Log to console in development
        //console.log(`Web Vital: ${name} - Delta: ${delta}ms - ID: ${id}`);
        
        // Here you would send to your analytics service
        // Example: Google Analytics 4
        if (hasAnalyticsConsent() && window.gtag) {
          window.gtag('event', name, {
            value: Math.round(name === 'CLS' ? delta * 1000 : delta),
            metric_id: id,
            metric_value: delta,
            metric_delta: delta,
          });
        }
      };
      
      getCLS(reportWebVital);
      getFID(reportWebVital);
      getLCP(reportWebVital);
      getFCP(reportWebVital);
      getTTFB(reportWebVital);
    })
    .catch(err => {
      console.warn('No se pudo cargar web-vitals:', err);
    });
} else {
  // En desarrollo, reportamos solo al console para facilitar pruebas
  console.log('Web Vitals monitoring disabled in development mode');
}

// Initialize development mode if needed
if (process.env.NODE_ENV === 'development' || process.env.VUE_APP_ENV === 'development') {
  // Initialize global storage for mock data
  window.devMockContracts = window.devMockContracts || {};
  window.devMockStorage = window.devMockStorage || {};
  window.devNextContractId = window.devNextContractId || 1;
  
  // Add dev helpers to window for easy console debugging
  window.devHelpers = {
    listContracts: () => {
      console.table(Object.values(window.devMockContracts));
      return Object.values(window.devMockContracts);
    },
    clearContracts: () => {
      window.devMockContracts = {};
      window.devNextContractId = 1;
     // console.log('Development contracts cleared');
    },
    resetDevMode: () => {
      sessionStorage.removeItem('dev_auth_user');
      window.devMockContracts = {};
      window.devMockStorage = {};
      window.devNextContractId = 1;
     // console.log('Development mode reset');
    }
  };
  
  
}

const app = createApp(App)
const head = createHead()  // Create head instance

app.config.globalProperties.$encodeSpecialChars = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[^\u0020-\u007E]/g, char => `&#${char.charCodeAt(0)};`);
}

app.mixin({
  mounted() {
    const metaCharset = document.querySelector('meta[charset]');
    if (metaCharset) {
      metaCharset.setAttribute('charset', 'utf-8');
    } else {
      const newMetaCharset = document.createElement('meta');
      newMetaCharset.setAttribute('charset', 'utf-8');
      document.head.appendChild(newMetaCharset);
    }
  }
})

// Setup redirect tracking after router initialization
setupRedirectTracking(router);

// Medir el tiempo de carga para optimización
if (process.env.NODE_ENV === 'development') {
  performance.mark('app-init-start');
}

const COLOIVING_HUB_PATH_PREFIXES = ['/encuentratucoliving', '/findyourcoliving', '/residencias'];

function isColivingHubPath(pathname) {
  return COLOIVING_HUB_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Prerender usa viewport desktop; en móvil el DOM estático no coincide con SearchPageLayout → remount limpio. */
function clearPrerenderedAppForMobileColivingHub() {
  if (typeof window === 'undefined') return;
  const root = document.getElementById('app');
  if (!root?.innerHTML?.trim()) return;
  const compact = !window.matchMedia('(min-width: 1024px)').matches;
  if (!compact || !isColivingHubPath(window.location.pathname)) return;
  root.replaceChildren();
  root.removeAttribute('data-v-app');
}

// Función para inicializar la aplicación con medidas de rendimiento
const initApp = async () => {
  // Inicializar i18n
  await Trans.initializeI18n()
  
  app.use(router)
     .use(head)  // Add head plugin
     .use(i18n)  // Añadir i18n

  setDefaultGoogleConsentMode()

  if (hasAnalyticsConsent()) {
    loadGoogleAnalytics(router)
  }

  if (hasAdvertisingConsent()) {
    loadGoogleAdSense()
  }
  
  // Inicializar directives globales
  app.directive('click-outside', vClickOutside) // <-- Registrar la directiva
  
  // Registrar manejador de errores global
  app.config.errorHandler = (err, vm, info) => {
    console.error('Error en la aplicación:', err);
    console.error('Componente:', vm);
    console.error('Info:', info);
    
    // Manejo específico para errores comunes
    if (err instanceof RangeError && err.message.includes('Invalid time value')) {
      console.warn('Error de fecha inválida detectado. Esto puede deberse a datos de fecha corruptos en Firestore.');
      console.warn('Considera revisar y limpiar los datos de fechas en la base de datos.');
      return; // No propagar este error específico
    }
    
    // En producción, podríamos enviar estos errores a un servicio de monitoreo
    if (process.env.NODE_ENV === 'production') {
      // Aquí se podría implementar algún sistema de reporte de errores
      // como Sentry, LogRocket, etc.
    }
  };
  
  clearPrerenderedAppForMobileColivingHub();

  // Montar la aplicación
  app.mount('#app');
  
  // Precarga inteligente después del montaje inicial
  setTimeout(() => {
    // Importar el sistema de precarga
    import('./precacheAssets')
      .then(module => {
        // Guardar referencia al módulo de precarga para usarlo más tarde
        app.config.globalProperties.$precache = module.default;
      })
      .catch(e => console.error('Error precargando assets:', e));
  }, 3000);
  
  // Terminar la medición del tiempo de inicialización
  if (process.env.NODE_ENV === 'development') {
    performance.mark('app-init-end');
    performance.measure('App Initialization', 'app-init-start', 'app-init-end');
    const measure = performance.getEntriesByName('App Initialization')[0];
    console.log(`Aplicación inicializada en ${measure.duration.toFixed(2)}ms`);
  }
};

// Iniciar la aplicación
initApp().catch(err => {
  console.error('Error fatal al inicializar la aplicación:', err);
  // Mostrar un mensaje de error al usuario
  document.getElementById('app').innerHTML = `
    <div style="text-align:center; padding: 20px; font-family: sans-serif;">
      <h1 style="color:#d32f2f">Error al cargar la aplicación</h1>
      <p>Ha ocurrido un error inesperado. Por favor, intenta recargar la página.</p>
      <button onclick="window.location.reload()" 
        style="background: #3f51b5; color: white; border: none; padding: 10px 15px; border-radius: 4px; cursor: pointer;">
        Recargar página
      </button>
    </div>
  `;
});